import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const rawKey = (base64String || "").trim();
  if (!rawKey) throw new Error("Clave VAPID vacía: reinicia el backend o configura VAPID_PUBLIC_KEY");
  const padding = "=".repeat((4 - (rawKey.length % 4)) % 4);
  const base64 = (rawKey + padding).replace(/-/g, "+").replace(/_/g, "/");
  let raw;
  try {
    raw = atob(base64);
  } catch {
    throw new Error("Clave VAPID inválida (no es base64 URL-safe)");
  }
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  // Clave pública VAPID = punto no comprimido P-256 (65 bytes, empieza por 0x04)
  if (out.length !== 65 || out[0] !== 0x04) {
    throw new Error("Clave VAPID pública con formato incorrecto. Regenera las claves en el servidor.");
  }
  return out;
}

function pushErrorMessage(err) {
  const msg = String(err?.message || err || "");
  if (/push service error|AbortError|Registration failed/i.test(msg)) {
    return "No se pudo registrar push en este navegador. Prueba en HTTPS o localhost, acepta el permiso y vuelve a activarlo (si cambiaste de entorno, desactiva y activa de nuevo).";
  }
  return msg || "Error al activar notificaciones push";
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function getPushSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function enablePush() {
  if (!pushSupported()) throw new Error("Este navegador no soporta notificaciones push");
  if (!window.isSecureContext && location.hostname !== "localhost" && location.hostname !== "127.0.0.1") {
    throw new Error("Las notificaciones push requieren HTTPS (o localhost)");
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permiso de notificaciones denegado");

  const { data } = await api.get("/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data?.public_key);
  const reg = await navigator.serviceWorker.ready;

  // Si ya hay suscripción con otra clave VAPID, Chrome lanza "push service error".
  // Siempre regeneramos la suscripción con la clave actual del backend.
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try {
      const json = existing.toJSON();
      await api.post("/push/unsubscribe", {
        endpoint: json.endpoint,
        keys: json.keys || { p256dh: "", auth: "" },
      }).catch(() => {});
      await existing.unsubscribe();
    } catch { /* ignore */ }
  }

  let sub;
  try {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  } catch (err) {
    throw new Error(pushErrorMessage(err));
  }

  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    expirationTime: json.expirationTime ?? null,
  });
  return true;
}

export async function disablePush() {
  const sub = await getPushSubscription();
  if (sub) {
    const json = sub.toJSON();
    try {
      await api.post("/push/unsubscribe", { endpoint: json.endpoint, keys: json.keys || { p256dh: "", auth: "" } });
    } catch { /* ignore */ }
    await sub.unsubscribe();
  } else {
    await api.post("/push/unsubscribe", {});
  }
  return true;
}
